/**
 * Socket.IO wrapper for the visitor side.
 *
 * The handshake carries the visitor token — the same credential REST uses. The
 * server joins rooms after verifying scope; we never name a room ourselves,
 * we only ask to subscribe to the conversation the token already proves.
 */
import { io } from 'socket.io-client';
import { SERVER_EVENTS, CLIENT_EVENTS } from '@superchat/shared/events';

export function createRealtime({ apiBase, onEvent, onStatus, debug = false }) {
  let socket = null;
  let conversationId = null;
  let typingTimer = null;

  function log(...args) {
    if (debug) console.info('[superchat:rt]', ...args);
  }

  function connect(token) {
    disconnect();

    socket = io(apiBase, {
      path: '/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      withCredentials: false,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 800,
      reconnectionDelayMax: 8000,
      timeout: 12000,
    });

    socket.on('connect', () => {
      log('connected');
      onStatus?.('connected');
      // Re-subscribe after every reconnect — room membership dies with the
      // socket, and the server re-checks ownership each time.
      if (conversationId) subscribe(conversationId);
    });

    socket.on('disconnect', (reason) => {
      log('disconnected', reason);
      onStatus?.('disconnected');
    });

    socket.on('connect_error', (err) => {
      log('connect_error', err.message);
      onStatus?.(err.message === 'unauthorized' ? 'unauthorized' : 'error');
    });

    for (const event of Object.values(SERVER_EVENTS)) {
      socket.on(event, (payload) => onEvent?.(event, payload));
    }
  }

  function subscribe(id) {
    conversationId = id;
    if (!socket?.connected) return;
    socket.emit(CLIENT_EVENTS.CONVERSATION_SUBSCRIBE, { conversationId: id }, (ack) => {
      if (!ack?.ok) log('subscribe rejected', ack?.error);
    });
  }

  /**
   * Tells agents the visitor reached for support.
   *
   * Fire-and-forget, and silently dropped when the socket is not up: an agent
   * alert is not worth queueing, and the panel opening again will say so.
   */
  function announceOpen() {
    if (!socket?.connected) return;
    socket.emit(CLIENT_EVENTS.CHAT_OPENED);
  }

  /** Debounced: one `true` while the visitor types, one `false` when they stop. */
  function setTyping(isTyping) {
    if (!socket?.connected || !conversationId) return;
    socket.emit(CLIENT_EVENTS.TYPING, { conversationId, isTyping });

    clearTimeout(typingTimer);
    if (isTyping) {
      typingTimer = setTimeout(() => {
        socket?.emit(CLIENT_EVENTS.TYPING, { conversationId, isTyping: false });
      }, 3000);
    }
  }

  function disconnect() {
    clearTimeout(typingTimer);
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }
  }

  return {
    connect,
    subscribe,
    setTyping,
    announceOpen,
    disconnect,
    get connected() {
      return Boolean(socket?.connected);
    },
  };
}

export default createRealtime;
